/**
 * Traza de un correlation id: el mismo hilo visto en todos los sistemas.
 *
 * Un pedido toca Janis, SAP, el WMS y el canal. El correlation id es lo que
 * cose esos saltos, y esta pantalla los pone en orden cronologico con su estado.
 * Es la respuesta a "¿donde se quedo este pedido?".
 *
 * Se actualiza sola cada 5 s mientras el pedido avanza. El switch es el mismo
 * del detalle y del flujo (preferencia `run`): son vistas de la misma corrida.
 */
import { useMemo } from 'react';
import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMonitorApi } from '../app/api-context';
import { AutoRefreshToggle } from '../components/AutoRefreshToggle';
import { ErrorBanner } from '../components/ErrorBanner';
import { EmptyState, LoadingBlock } from '../components/Feedback';
import { DirectionBadge, StatusBadge } from '../components/StatusBadge';
import { useAutoRefreshPreference } from '../hooks/use-auto-refresh-preference';
import { usePolledResource } from '../hooks/use-polled-resource';
import { REFRESH_INTERVAL_MS } from '../lib/auto-refresh';
import { flowLabel, systemLabel } from '../lib/catalogs';
import { formatDateTime } from '../lib/format';

export function TracePage(): ReactElement {
  const api = useMonitorApi();
  const { correlationId } = useParams<{ correlationId: string }>();
  const id = correlationId ?? '';
  const [autoRefresh, setAutoRefresh] = useAutoRefreshPreference('run');
  const resource = usePolledResource(() => api.getTrace(id), [api, id], {
    intervalMs: REFRESH_INTERVAL_MS.trace,
    enabled: autoRefresh,
  });

  const hops = useMemo(() => {
    const messages = resource.data ?? [];
    return [...messages].sort((left, right) => left.receivedAt.localeCompare(right.receivedAt));
  }, [resource.data]);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <p className="muted">
            <Link to="/mensajes">← Mensajes</Link>
            {' · '}
            <Link to={`/flujo/${id}`}>Ver como flujo</Link>
          </p>
          <h1>Traza del pedido</h1>
          <p className="page__subtitle mono">{id}</p>
        </div>
        <div className="row">
          <AutoRefreshToggle
            enabled={autoRefresh}
            onChange={setAutoRefresh}
            intervalMs={REFRESH_INTERVAL_MS.trace}
            updatedAt={resource.updatedAt}
            failing={resource.error !== null}
          />
          <button type="button" className="button" onClick={resource.reload}>
            Actualizar
          </button>
        </div>
      </header>

      <ErrorBanner error={resource.error} onRetry={resource.reload} />

      <section className="panel">
        <header className="panel__header">
          <h2>Saltos entre sistemas ({hops.length})</h2>
        </header>
        <div className="panel__body">
          {resource.loading && resource.data === null ? (
            <LoadingBlock />
          ) : hops.length === 0 ? (
            <EmptyState
              title="No hay mensajes con este correlation id."
              hint="Puede que el hilo viaje con otra referencia externa (Altanet y SAP la llevan como texto)."
            />
          ) : (
            <ol className="trace">
              {hops.map((hop) => (
                <li className="trace__hop" key={hop.id}>
                  <span className="trace__when">{formatDateTime(hop.receivedAt)}</span>
                  <div className="trace__what">
                    <div className="row">
                      <strong>{systemLabel(hop.system)}</strong>
                      <DirectionBadge direction={hop.direction} />
                      <StatusBadge status={hop.status} />
                      <span className="mono muted">{hop.eventType ?? 'sin evento'}</span>
                    </div>
                    <div className="muted">{flowLabel(hop.flow)}</div>
                    {hop.lastError === null ? null : (
                      <div className="mono" style={{ color: 'var(--danger)' }}>
                        {hop.lastError}
                      </div>
                    )}
                    <div>
                      <Link to={`/mensajes/${hop.id}`}>Ver payloads e intentos</Link>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>
    </div>
  );
}
